<template>
    <section v-if="questions.length" :id="id" class="faq">
        <a-dangerous-html
            v-if="data.title"
            class="faq-title"
            tag="h2"
            :content="data.title"
        />
        <a-dangerous-html
            v-if="data.lead"
            class="faq-lead"
            tag="p"
            :content="data.lead"
        />
        <a-accordion class="faq-accordion" one-only-mode>
            <a-accordion-item
                v-for="(item, i) in questions"
                :key="i"
                class="faq-item"
                title-tag="h3"
            >
                <template #title>
                    <a-glyph class="icon" name="icon-pointer-right" />
                    <a-dangerous-html tag="span" :content="item.question" />
                </template>
                <a-dangerous-html class="faq-answer" :content="item.answer" />
            </a-accordion-item>
        </a-accordion>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AAccordionItem from '@core/components/accordion/accordion-item.vue';
import AAccordion from '@core/components/accordion/accordion.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import type { BodyVariant, FaqData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockFaq',
});

const props = defineProps<{
    id?: string;
    data: FaqData;
    variant: BodyVariant;
}>();

const questions = computed(() => (props.data.questions || []).filter((item) => item.question && item.answer));
</script>

<style lang="pcss" scoped>
.body-renderer .faq {
    margin: 48px 0;
}

.body-renderer .faq-title {
    @mixin display;

    font-weight: 700;
    margin-bottom: 16px;
    color: var(--av-nav-primary);
}

.body-renderer .faq-lead {
    @mixin lead;

    margin-bottom: 24px;
    color: var(--av-fixed-secondary);
}

.body-renderer .faq-accordion {
    margin-bottom: 24px;

    :deep(.a-accordion-item:last-of-type) {
        margin-bottom: 0;
    }
}

.body-renderer .faq-item {
    :deep(.title) {
        @mixin lead;

        font-weight: 700;
        align-items: flex-start;
        padding: 16px 0;
        color: var(--av-nav-primary);
    }

    :deep(.icon) {
        margin-inline-end: 12px;
        fill: var(--av-brand-secondary);
    }

    :deep(.content) {
        @mixin paragraph;
    }

    :deep(.content-wrapper) {
        padding: 8px 0 24px 32px;
    }
}

.body-renderer .faq-answer {
    @mixin paragraph;

    color: var(--av-fixed-secondary);

    :deep(p) {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    :deep(ol),
    :deep(ul) {
        margin: 16px 0;
        padding-inline-start: 24px;
    }

    :deep(ol) {
        list-style-type: decimal;
    }

    :deep(li) {
        display: list-item;
        margin-bottom: 8px;
        padding-inline-start: 4px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.variant-tru {
    .faq {
        margin: 40px 0;
    }

    .faq-title {
        @mixin title-accent;
    }

    .faq-lead {
        @mixin paragraph;

        color: #000;
    }

    .faq-item :deep(.title) {
        @mixin paragraph-accent;
    }

    .faq-answer {
        color: #000;
    }
}
</style>
